import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import VotesStore from "@apps/reviews/stores/VotesStore";
import {
    reviewsAuthApiClientToken,
    reviewsIconCollectionManagerToken,
} from "@apps/reviews/tokens";
import ProductReviewsMiniSliderWidgetReviewsQuery from "@apps/reviews/widgets/product_reviews_mini_slider_widget/queries/ProductReviewsMiniSliderWidgetReviewsQuery";
import ProductReviewsMiniSliderWidgetReviewsStore from "@apps/reviews/widgets/product_reviews_mini_slider_widget/stores/ProductReviewsMiniSliderWidgetReviewsStore";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
import { I18nModule } from "@modules/i18n/I18nModule";
import { container, injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import ReviewsGalleryManager from "../reviews_gallery_widget/ReviewsGalleryManager";
import ProductReviewsMiniSliderWidgetComponent from "./ProductReviewsMiniSliderWidget.svelte";
import { ProductReviewsMiniSliderWidgetConfigInterface } from "./ProductReviewsMiniSliderWidgetConfig";
import ProductReviewsMiniSliderWidgetReviewsService from "./services/ProductReviewsMiniSliderWidgetReviewsService";

@injectable()
class ProductReviewsMiniSliderWidget implements WidgetInterface {
    private readonly reviewsStore: ProductReviewsMiniSliderWidgetReviewsStore;
    private readonly reviewsService: ProductReviewsMiniSliderWidgetReviewsService;
    private readonly reviewsQuery: ProductReviewsMiniSliderWidgetReviewsQuery;
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private readonly reviewsGalleryManager: ReviewsGalleryManager;

    constructor(
        private readonly config: ProductReviewsMiniSliderWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly productIdManager: ProductIdManagerInterface
    ) {
        this.logger.debug(
            "ProductReviewsMiniSliderWidget is initialized",
            config
        );
        const authApiClient = container.resolve(reviewsAuthApiClientToken);

        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "reviews.ProductReviewsMiniSliderWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.ProductReviewsMiniSliderWidget"
            )
        );

        // Reviews
        this.reviewsStore = new ProductReviewsMiniSliderWidgetReviewsStore();
        this.reviewsService = new ProductReviewsMiniSliderWidgetReviewsService(
            this.reviewsStore,
            container.resolve(VotesStore),
            authApiClient,
            this.logger
        );
        this.reviewsQuery = new ProductReviewsMiniSliderWidgetReviewsQuery(
            this.reviewsStore
        );

        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );

        this.reviewsGalleryManager = container.resolve(ReviewsGalleryManager);
    }

    async init(): Promise<this> {
        const customizationManager = container.resolve(CustomizationManager);
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.PRODUCT_REVIEWS_MINI_SLIDER_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );

        new ProductReviewsMiniSliderWidgetComponent({
            target: shadowRoot || element,
            props: {
                productIdManager: this.productIdManager,

                reviewsQuery: this.reviewsQuery,
                reviewsService: this.reviewsService,

                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                lowestRating: this.config.lowestRating,
                onlyPinnedReviews: this.config.onlyPinnedReviews,
                reviewsGalleryManager: this.reviewsGalleryManager,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
            context: contextMap,
        });

        return this;
    }
}

export default ProductReviewsMiniSliderWidget;
